import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { DigitalAccount } from '../../types/digitalPayment';

interface DigitalAccountsOverviewProps {
  accounts: DigitalAccount[];
}

function DigitalAccountsOverview({ accounts }: DigitalAccountsOverviewProps) {
  const totalBalance = accounts.reduce((sum, account) => sum + account.balance, 0);
  const monthlyIncoming = accounts.reduce(
    (sum, account) => sum + account.monthlyActivity.incoming,
    0
  );
  const monthlyOutgoing = accounts.reduce(
    (sum, account) => sum + account.monthlyActivity.outgoing,
    0
  );

  const chartData = accounts.map((account) => ({
    name: account.platform.toUpperCase().replace('_', ' '),
    value: account.balance,
  }));

  const COLORS = ['#B100FF', '#10B981', '#F59E0B', '#6366F1'];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Digital Payments Overview
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => [
                  `$${value.toLocaleString()}`,
                  'Balance',
                ]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Balance</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                ${totalBalance.toLocaleString()}
              </p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Net Flow</p>
              <p className={`text-xl font-bold ${
                monthlyIncoming - monthlyOutgoing >= 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                ${(monthlyIncoming - monthlyOutgoing).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Monthly Activity</p>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-900 dark:text-white">Incoming</span>
                <span className="text-sm font-medium text-green-500">
                  +${monthlyIncoming.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-900 dark:text-white">Outgoing</span>
                <span className="text-sm font-medium text-red-500">
                  -${monthlyOutgoing.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {accounts.map((account, index) => (
              <div
                key={account.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {account.platform.replace('_', ' ')}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    ${account.balance.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {((account.balance / totalBalance) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DigitalAccountsOverview;